import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';

import * as Hooks from '../hooks';
import * as Types from '../types';

const updateSchema = z.object({
  id: z.string(),
  title: z.string().min(1, 'Title is required').optional(),
  description: z.string().min(1, 'Description is required').optional(),
  duration: z.number().min(1, 'Duration must be at least 1 minute').optional(),
  passingScore: z.number().min(0).max(100, 'Passing score must be between 0 and 100').optional(),
  status: z.enum(['DRAFT', 'ACTIVE', 'ARCHIVED']).optional(),
  settings: z
    .object({
      shuffleQuestions: z.boolean().optional(),
      shuffleAnswers: z.boolean().optional(),
      showResultsImmediately: z.boolean().optional(),
      allowReview: z.boolean().optional(),
      proctoringEnabled: z.boolean().optional(),
      requireCamera: z.boolean().optional(),
      requireMicrophone: z.boolean().optional(),
      fullScreenMode: z.boolean().optional(),
      preventCopyPaste: z.boolean().optional(),
    })
    .optional(),
});

type FormValues = z.infer<typeof updateSchema>;

type FormStatus = 'DRAFT' | 'ACTIVE' | 'ARCHIVED';

interface IProps {
  id: string;
  initialValues?: Partial<Types.IEntity.TestDetails>;
  onSuccess?: (data: any) => void;
  onError?: (error: string) => void;
  children: (props: {
    form: ReturnType<typeof useForm<FormValues>>;
    isSubmitting: boolean;
    handleSubmit: (e?: React.BaseSyntheticEvent) => void;
  }) => JSX.Element;
}

const mapStatusToApi = (status?: string): FormStatus => {
  if (!status) return 'DRAFT';
  const s = String(status).toUpperCase();
  if (s === 'PUBLISHED' || s === 'ACTIVE') return 'ACTIVE';
  if (s === 'ARCHIVED') return 'ARCHIVED';
  return 'DRAFT';
};

const Update: React.FC<IProps> = ({ id, initialValues, onSuccess, onError, children }) => {
  const mutation = Hooks.useUpdate();

  const defaultStatus: FormStatus = mapStatusToApi(initialValues?.status as string | undefined);

  const form = useForm<FormValues>({
    resolver: zodResolver(updateSchema),
    defaultValues: {
      id,
      title: initialValues?.title || '',
      description: initialValues?.description || '',
      duration: initialValues?.duration || 60,
      passingScore: initialValues?.passingScore || 70,
      status: defaultStatus,
      settings: initialValues?.settings || {
        shuffleQuestions: false,
        shuffleAnswers: false,
        showResultsImmediately: true,
        allowReview: true,
        proctoringEnabled: false,
        requireCamera: false,
        requireMicrophone: false,
        fullScreenMode: false,
        preventCopyPaste: false,
      },
    },
  });

  // Update form values when initialValues change
  useEffect(() => {
    if (initialValues) {
      const resetStatus: FormStatus = mapStatusToApi(initialValues.status as string | undefined);
      form.reset({
        id,
        title: initialValues.title,
        description: initialValues.description,
        duration: initialValues.duration,
        passingScore: initialValues.passingScore,
        status: resetStatus,
        settings: initialValues.settings,
      });
    }
  }, [initialValues, id, form]);

  const onSubmit = form.handleSubmit((values) => {
    mutation.mutate(
      {
        id: values.id,
        title: values.title,
        description: values.description,
        status: values.status,
        settings: values.settings as Record<string, unknown> | undefined,
      },
      {
        onSuccess: (data) => {
          onSuccess?.(data);
        },
        onError: (error: any) => {
          onError?.(error?.response?.data?.message || 'Failed to update test');
        },
      }
    );
  });

  return children({
    form: form as ReturnType<typeof useForm<FormValues>>,
    isSubmitting: mutation.isPending,
    handleSubmit: onSubmit,
  });
};

export default Update;
